import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Paper,
	Button,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	IconButton,
	TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// cmps
import ListItemContainer from "../ListItemContainer";
import CreateGroup from "./create-group";
import Svg from "../svg";
import Toast from "../Toast";
// hooks
import useSend from "../../hooks/useSend";
import useSorter from "../../hooks/useSorter";
// services
import User from "../../services/user";
import { icons } from "@local/theme";
// types
import type { MouseEvent } from "react";
import type { CmdDeleteGroup } from "../../types/gupport";
import type { FilteredGroup, GroupName, FilteredGroups } from "../../types/firmware-groups";

type Props = Readonly<{
	selectedGroupName: GroupName | undefined;
	group: FilteredGroup;
	groups: FilteredGroups;
	onGroupClick: (groupName: GroupName | undefined) => void;
	fetchGroups: () => void;
}>;

const GroupCategory = ({ selectedGroupName, group, groups, onGroupClick, fetchGroups }: Props) => {
	const send = useSend();
	const theme = useTheme();
	const { t } = useTranslation();
	const sortAlphabetically = useSorter();

	const [expanded, setExpanded] = useState(false);
	const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);
	const [selectedGroupToDelete, setSelectedGroupToDelete] = useState<GroupName | null>(null);
	const [deleteGroupName, setDeleteGroupName] = useState<GroupName | undefined>(undefined);
	const [inProgress, setInProgress] = useState(false);
	const [showGroupDeleteSuccess, setShowGroupDeleteSuccess] = useState<number | undefined>(undefined);
	const [showGroupDeleteError, setShowGroupDeleteError] = useState<number | undefined>(undefined);

	const showGroupDeleteDialog = (event: MouseEvent<HTMLElement>, groupName: GroupName) => {
		event.stopPropagation();
		setSelectedGroupToDelete(groupName);
		setDeleteGroupName(undefined);
		setShowDeleteGroupDialog(true);
	};

	const handleDeleteGroup = () => {
		if (deleteGroupName === undefined) {
			return;
		}
		setInProgress(true);
		const cmd = {
			action: "deleteGroup",
			group: deleteGroupName,
		} as const satisfies CmdDeleteGroup;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowGroupDeleteError(undefined);
				setShowGroupDeleteSuccess(Date.now());
				if (selectedGroupName === deleteGroupName) {
					onGroupClick(undefined);
				}
				fetchGroups();
			} else {
				setShowGroupDeleteError(Date.now());
			}
			setInProgress(false);
			setShowDeleteGroupDialog(false);
			setSelectedGroupToDelete(null);
			setDeleteGroupName(undefined);
		});
	};

	return (
		<>
			<Paper component="section" className="sub-section" sx={{ width: 300 }}>
				<List disablePadding={true}>
					<ListItemButton onClick={() => (setExpanded(!expanded))} sx={{ padding: "12px 16px" }}>
						<ListItemIcon><icons.Storage /></ListItemIcon>
						<ListItemText primary={group.category} />
						<ListItemContainer onClick={((event) => (event.stopPropagation()))}>
							<CreateGroup groupCategory={group.category} groups={groups} fetchGroups={fetchGroups} />
						</ListItemContainer>
						<ListItemIcon className="ListItemIcon-right">
							{expanded
								? <icons.ExpandLess htmlColor={theme.vars.palette.text.secondary} />
								: <Svg src="navigation/arrowDropDown.svg" color={theme.vars.palette.text.secondary} />
							}
						</ListItemIcon>
					</ListItemButton>
					<Collapse in={expanded} timeout="auto">
						<List disablePadding={true}>
							{group.names.toSorted(sortAlphabetically).map((groupName) => (
								<ListItemButton
									key={groupName}
									selected={groupName === selectedGroupName}
									sx={{ pl: 4 }}
									onClick={() => (onGroupClick(groupName))}
								>
									<ListItemText primary={groupName} />
									{User.hasLevel("fw_group_manage") &&
										<ListItemContainer onClick={(event) => (showGroupDeleteDialog(event, groupName))}>
											<IconButton
												className="btn-group-delete-dialog-open"
												color="danger"
												aria-label={t("firmwareGroups.actions.deleteGroup.dialogTitle")}
											>
												<Svg src="actions/delete.svg" />
											</IconButton>
										</ListItemContainer>
									}
								</ListItemButton>
							))}
						</List>
					</Collapse>
				</List>
			</Paper>
			<Dialog
				fullWidth={true}
				open={showDeleteGroupDialog}
				onClose={() => (setShowDeleteGroupDialog(false))}
			>
				<DialogTitle>{t("firmwareGroups.actions.deleteGroup.dialogTitle")}</DialogTitle>
				<DialogContent>
					<TextField
						placeholder={t("firmwareGroups.actions.deleteGroup.placeholder", { GROUP_NAME: selectedGroupToDelete })}
						fullWidth={true}
						value={deleteGroupName}
						onChange={(event) => (setDeleteGroupName(event.target.value as GroupName))}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => (setShowDeleteGroupDialog(false))}>{t("dialog.cancel")}</Button>
					<Button
						color="danger"
						disabled={deleteGroupName !== selectedGroupToDelete}
						loading={inProgress}
						onClick={handleDeleteGroup}
					>
						{t("dialog.delete")}
					</Button>
				</DialogActions>
			</Dialog>
			<Toast
				autoHideDuration={3000}
				severity="success"
				open={showGroupDeleteSuccess}
				onClose={setShowGroupDeleteSuccess}
				message={t("firmwareGroups.actions.deleteGroup.successMsg")}
			/>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGroupDeleteError}
				onClose={setShowGroupDeleteError}
				message={t("toast.genericErrorMsg")}
			/>
		</>
	);
};

export default GroupCategory;
